import React from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Card, CardContent, Grid, MenuItem, Select, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ExportButtons from '../../services/sextforce/metrics/account/performanceStats/ExportButtons';

type AnalyticsRangePreset = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';
type AnalyticsComparisonPreset = 'none' | 'previousPeriod';
type AnalyticsAmountType = 'gross' | 'net';

type AnalyticsControlsBarProps = {
    isLargeScreen: boolean;
    rangePreset: AnalyticsRangePreset;
    comparisonPreset: AnalyticsComparisonPreset;
    amountType: AnalyticsAmountType;
    activeMainRangeLabel: string;
    onRangePresetChange: (value: AnalyticsRangePreset) => void;
    onOpenCustomRangeDialog: () => void;
    onComparisonPresetChange: (value: AnalyticsComparisonPreset) => void;
    onAmountTypeChange: (value: AnalyticsAmountType) => void;
    analyticsReportExportData: Array<Record<string, string | number>>;
    reportFilename: string;
};

const AnalyticsControlsBar = React.memo(
    ({
        isLargeScreen,
        rangePreset,
        comparisonPreset,
        amountType,
        activeMainRangeLabel,
        onRangePresetChange,
        onOpenCustomRangeDialog,
        onComparisonPresetChange,
        onAmountTypeChange,
        analyticsReportExportData,
        reportFilename,
    }: AnalyticsControlsBarProps) => (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            fullWidth={!isLargeScreen}
                            size="small"
                            value={rangePreset}
                            onChange={(_, value: AnalyticsRangePreset | null) => {
                                if (value) {
                                    onRangePresetChange(value);
                                }
                            }}
                        >
                            <ToggleButton value="7d" sx={{ minWidth: 50 }}>
                                7D
                            </ToggleButton>
                            <ToggleButton value="30d" sx={{ minWidth: 50 }}>
                                30D
                            </ToggleButton>
                            <ToggleButton value="90d" sx={{ minWidth: 50 }}>
                                90D
                            </ToggleButton>
                            <ToggleButton value="1y" sx={{ minWidth: 50 }}>
                                1Y
                            </ToggleButton>
                            <ToggleButton value="all" sx={{ minWidth: 50 }}>
                                ALL
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="medium"
                            fullWidth={!isLargeScreen}
                            onClick={onOpenCustomRangeDialog}
                            startIcon={<CalendarTodayIcon fontSize="small" />}
                            endIcon={<ExpandMoreIcon fontSize="small" />}
                            sx={{
                                minWidth: 280,
                                height: 37,
                                justifyContent: 'space-between',
                                textTransform: 'none',
                                color: 'text.primary',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                            }}
                        >
                            {activeMainRangeLabel}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Select
                            size="small"
                            fullWidth={!isLargeScreen}
                            value={comparisonPreset}
                            onChange={(e) => onComparisonPresetChange(e.target.value as AnalyticsComparisonPreset)}
                            disabled={rangePreset === 'all'}
                            sx={{ minWidth: 170 }}
                        >
                            <MenuItem value="none">Compare: None</MenuItem>
                            <MenuItem value="previousPeriod">Compare: Previous Period</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs="auto">
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            size="small"
                            value={amountType}
                            onChange={(_, value: AnalyticsAmountType | null) => {
                                if (value) {
                                    onAmountTypeChange(value);
                                }
                            }}
                        >
                            <ToggleButton value="gross" sx={{ minWidth: 60 }}>
                                Gross
                            </ToggleButton>
                            <ToggleButton value="net" sx={{ minWidth: 60 }}>
                                Net
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs="auto">
                        <ExportButtons data={analyticsReportExportData} componentType="report" filename={reportFilename} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    ),
);

AnalyticsControlsBar.displayName = 'AnalyticsControlsBar';

export default AnalyticsControlsBar;
